/**
 * Triggers a direct client-side file download using a temporary Blob URL.
 * Falls back gracefully to triggering a direct anchor click if fetch fails (e.g. CORS restrictions).
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  if (!url) return;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Direct download failed, falling back to direct link trigger:', error);
    // Fallback for CORS-restricted origins or network restrictions
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Triggers a direct client-side download from an existing in-memory Blob.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  if (!blob) return;

  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
